import { useState } from 'react';

import { listGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  applyGiftPartner,
  createGiftPartner,
  requestGiftPartnerChange,
} from '@shared/api/giftPartner/giftPartner.controller';
import {
  useAdminGiftPartners,
  useGiftPartnerAction,
  useGiftPartnerOverview,
} from '@shared/api/giftPartner/giftPartner.hook';
import {
  GiftPartner,
  GiftPartnerApplyDto,
  GiftPartnerOverview,
} from '@shared/api/giftPartner/giftPartner.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { Alert, Button, Input, Modal, Select, Tag } from 'antd';
import { useQuery } from 'react-query';
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { GiftPartnerApplicantGuide } from './GiftPartnerApplicantGuide';
import { GiftPartnerDetail } from './GiftPartnerDetail';
import { GiftPartnerGuideResources } from './guide/GiftPartnerGuide';
import {
  ApplicationForm,
  PartnerError,
  PartnerSecret,
  PartnerSection,
  SecretModal,
} from './GiftPartnerForms';
import {
  brandIds,
  brandUnavailable,
  dateLabel,
  OverviewBrand,
  partnerStatus,
} from './giftPartner.utils';

const BRAND_PATH = '/gift-brand/api-integration';
const ADMIN_PATH = '/gift-seller/api-integration';
const ADMIN_LIST_PATH = '/gift-seller/brand?tab=api-integration';

export const GiftBrandIntegrationPage = () => {
  const { isAdmin, isGiftBrandSeller, userProfile } = useAuth();
  const query = useGiftPartnerOverview();
  if (isAdmin())
    return (
      <Alert
        type="info"
        message={<Link to={ADMIN_LIST_PATH}>API 연동은 관리자 화면에서 관리하세요.</Link>}
      />
    );
  if (!isGiftBrandSeller()) return <Navigate to="/gift-brand/login" replace />;
  if (query.isLoading) return <p role="status">브랜드 연동 현황을 불러오는 중입니다.</p>;
  if (query.isError) return <LoadError retry={() => void query.refetch()} />;
  if (query.data === null) return <Navigate to="/gift-brand/delivery" replace />;
  if (!query.data) return null;
  return <BrandIntegrationContent key={userProfile?._id} overview={query.data} />;
};

const BrandIntegrationContent = ({ overview }: { overview: GiftPartnerOverview }) => {
  const { partnerId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { run, busy, error } = useGiftPartnerAction();
  const [notice, setNotice] = useState('');
  const [addition, setAddition] = useState<{ partnerId: string; brand: OverviewBrand }>();
  const { brands, partners } = overview;
  const isNew = partnerId === 'new';
  const selected = partners.find((p) => p._id === partnerId);
  const free = brands.filter((b) => !brandUnavailable(b));
  const approved = partners.filter((p) => p.status === 'Approved' && p.isEnabled);
  const rejected = partners.filter((p) => p.status === 'Rejected');
  const single = brands.length === 1 ? brands[0] : undefined;
  if (selected)
    return (
      <div className="space-y-4">
        {notice && (
          <Alert type="success" message={notice} closable onClose={() => setNotice('')} />
        )}
        <GiftPartnerDetail
          key={selected._id}
          partner={selected}
          brands={brands}
          role={LowerRole.GiftBrandSeller}
          backPath={BRAND_PATH}
        />
      </div>
    );
  if (partnerId && !isNew)
    return (
      <Alert
        type="warning"
        message="연동을 찾을 수 없거나 관리 범위가 변경되었습니다."
        description={<Link to={BRAND_PATH}>브랜드 현황으로 돌아가기</Link>}
      />
    );
  if (
    !isNew &&
    single &&
    !single.managedElsewhere &&
    (single.partner || single.pendingPartner)
  )
    return (
      <Navigate to={`${BRAND_PATH}/${single.partner || single.pendingPartner}`} replace />
    );
  const retry = partners.find(
    (p) => p._id === params.get('retry') && p.status === 'Rejected',
  );
  const chosenBrand = params.get('brand');
  const initialIds = chosenBrand
    ? free.filter((b) => b._id === chosenBrand).map((b) => b._id)
    : retry
    ? free.filter((b) => brandIds(retry).includes(b._id)).map((b) => b._id)
    : undefined;
  const showForm = isNew || (!!single && !single.managedElsewhere);
  const additionPartner = partners.find((p) => p._id === addition?.partnerId);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">API 연동</h1>
          <p className="mt-1 text-sm text-gray-600">
            서버 1대에 브랜드 여러 개를 묶고, 연동키 하나로 발주를 받습니다.
          </p>
        </div>
        {!showForm && (
          <Button
            type="primary"
            className="gift-partner-primary"
            disabled={!free.length}
            onClick={() => navigate(`${BRAND_PATH}/new`)}
          >
            + 연동 신청
          </Button>
        )}
      </div>
      {(showForm || !partners.some((p) => p.status !== 'Rejected')) && (
        <GiftPartnerApplicantGuide />
      )}
      {!showForm && <GiftPartnerGuideResources />}
      <PartnerError error={error} />
      {notice && (
        <Alert
          type="success"
          showIcon
          message={notice}
          closable
          onClose={() => setNotice('')}
        />
      )}
      {brands.length === 0 && (
        <Alert
          type="info"
          message={
            <>
              담당 브랜드가 없습니다. <Link to="/gift-brand/enquiry">문의하기</Link>로
              연락해 주세요.
            </>
          }
        />
      )}
      {showForm ? (
        <PartnerSection title="연동 신청">
          {free.length ? (
            <ApplicationForm
              key={`${params.toString()}:${single?._id ?? ''}`}
              brands={brands}
              initialIds={initialIds}
              initial={
                retry
                  ? {
                      name: retry.name,
                      allowedIps: retry.allowedIps,
                      webhookUrl: retry.webhook?.url,
                      contact: retry.contact,
                    }
                  : undefined
              }
              busy={busy}
              error={error}
              onCancel={isNew ? () => navigate(BRAND_PATH) : undefined}
              onSubmit={(dto) =>
                void run(
                  async () => {
                    const result = await applyGiftPartner({
                      ...dto,
                      name: dto.name?.trim() || undefined,
                      giftItemBrands: dto.giftItemBrands,
                    });
                    navigate(`${BRAND_PATH}/${result._id}`);
                  },
                  () =>
                    setNotice(
                      '연동 신청이 접수되었습니다. 아래에서 API 키를 발급받아 주세요.',
                    ),
                )
              }
            />
          ) : (
            <Alert
              type="info"
              message="신청할 수 있는 미연동 브랜드가 없습니다."
              description={<Link to={BRAND_PATH}>브랜드 현황으로 돌아가기</Link>}
            />
          )}
        </PartnerSection>
      ) : (
        <>
          {brands.length > 1 && (
            <PartnerSection title="브랜드 현황">
              <div
                role="table"
                aria-label="브랜드별 API 연동 현황"
                className="divide-y divide-gray-200"
              >
                <div
                  role="row"
                  className="hidden gap-2 pb-2 text-xs text-gray-500 sm:grid sm:grid-cols-[1fr_1.5fr_2fr]"
                >
                  <span role="columnheader">브랜드</span>
                  <span role="columnheader">연동 상태</span>
                  <span role="columnheader" className="text-right">
                    관리
                  </span>
                </div>
                {brands.map((brand) => {
                  const p = partners.find(
                    (item) => item._id === (brand.partner || brand.pendingPartner),
                  );
                  return (
                    <div
                      key={brand._id}
                      role="row"
                      className="grid items-center gap-2 py-3 sm:grid-cols-[1fr_1.5fr_2fr]"
                    >
                      <strong role="cell" className="break-words text-sm">
                        {brand.name}
                      </strong>
                      <div role="cell" className="break-words text-sm text-gray-600">
                        {brand.managedElsewhere
                          ? '다른 담당 범위에서 관리 중'
                          : brand.partner
                          ? `${p ? partnerStatus(p) : '연동됨'} · ${p?.name ?? ''}`
                          : brand.pendingPartner
                          ? `추가 승인 대기 · ${p?.name ?? ''}`
                          : '미연동'}
                      </div>
                      <div role="cell" className="flex flex-wrap gap-2 sm:justify-end">
                        {brand.managedElsewhere ? (
                          <Link
                            to="/gift-brand/enquiry"
                            className="text-sm text-indigo-700"
                          >
                            문의하기
                          </Link>
                        ) : brand.partner || brand.pendingPartner ? (
                          <Link
                            to={`${BRAND_PATH}/${brand.partner || brand.pendingPartner}`}
                            className="text-sm text-indigo-700 underline"
                          >
                            관리
                          </Link>
                        ) : (
                          <>
                            {approved.length > 0 && (
                              <Select<string | null>
                                aria-label={`${brand.name} 기존 연동에 추가`}
                                placeholder="기존 연동에 추가"
                                value={null}
                                className="w-44"
                                options={approved.map((p) => ({
                                  value: p._id,
                                  label: p.name,
                                }))}
                                onChange={(id) =>
                                  id && setAddition({ partnerId: id, brand })
                                }
                              />
                            )}
                            <Button
                              size="small"
                              onClick={() =>
                                navigate(`${BRAND_PATH}/new?brand=${brand._id}`)
                              }
                            >
                              새로 신청
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </PartnerSection>
          )}
          {single?.managedElsewhere && (
            <Alert
              type="info"
              message={`${single.name}: 다른 담당 범위에서 관리 중`}
              description={<Link to="/gift-brand/enquiry">문의하기</Link>}
            />
          )}
          <PartnerSection title="연동 목록">
            {partners.filter((p) => p.status !== 'Rejected').length === 0 && (
              <p className="text-sm text-gray-500">등록된 연동이 없습니다.</p>
            )}
            {partners
              .filter((p) => p.status !== 'Rejected')
              .map((p) => (
                <PartnerListRow
                  key={p._id}
                  partner={p}
                  brands={brands}
                  basePath={BRAND_PATH}
                />
              ))}
          </PartnerSection>
        </>
      )}
      {rejected.length > 0 && (
        <details className="rounded-lg border border-gray-200 bg-white p-4">
          <summary className="cursor-pointer text-sm">
            반려된 신청 {rejected.length}건
          </summary>
          <div className="mt-3 space-y-2">
            {rejected.map((p) => (
              <PartnerListRow
                key={p._id}
                partner={p}
                brands={brands}
                basePath={BRAND_PATH}
              />
            ))}
          </div>
        </details>
      )}
      <Modal
        okButtonProps={{ className: 'gift-partner-primary' }}
        open={!!addition && !!additionPartner}
        title={`${additionPartner?.name ?? ''} · 브랜드 추가 요청`}
        confirmLoading={busy}
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
        cancelButtonProps={{ disabled: busy }}
        onCancel={() => setAddition(undefined)}
        okText="추가 승인 요청"
        cancelText="취소"
        onOk={() =>
          addition &&
          additionPartner &&
          void run(
            async () => {
              // Keep pending removals/additions when replacing the complete requested scope.
              const ids =
                additionPartner.pendingChange?.giftItemBrands ??
                brandIds(additionPartner);
              await requestGiftPartnerChange(additionPartner._id, {
                giftItemBrands: Array.from(new Set([...ids, addition.brand._id])),
              });
            },
            () => {
              setAddition(undefined);
              setNotice(
                '추가 승인 요청을 보냈습니다. 승인 전까지 새 브랜드의 발주는 API·웹훅으로 나가지 않습니다.',
              );
            },
          )
        }
      >
        <div className="space-y-3">
          <p>
            <strong>{addition?.brand.name}</strong>의 발주가 {additionPartner?.name}의
            같은 연동키·같은 콜백 URL로 함께 전달됩니다. 각 건의 item.brandName으로 구분할
            수 있습니다.
          </p>
          <Alert
            type="info"
            message="관리자 승인 전까지 새 브랜드의 발주는 API·웹훅으로 나가지 않습니다. 기존 브랜드와 키는 그대로 유지됩니다."
          />
          {additionPartner?.pendingChange && (
            <p>기존 승인 대기 중인 브랜드 변경을 유지한 전체 목록으로 요청합니다.</p>
          )}
          <PartnerError error={error} />
        </div>
      </Modal>
    </div>
  );
};

const PartnerListRow = ({
  partner: p,
  brands,
  basePath,
}: {
  partner: GiftPartner;
  brands: { _id: string; name: string }[];
  basePath: string;
}) => {
  const names = (ids: string[]) =>
    ids.map((id) => brands.find((b) => b._id === id)?.name ?? id).join(' · ');
  const additions =
    p.pendingChange?.giftItemBrands?.filter((id) => !brandIds(p).includes(id)) ?? [];
  return (
    <Link
      to={`${basePath}/${p._id}`}
      className="block space-y-2 rounded border border-gray-200 p-3 text-gray-900 hover:bg-gray-50"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <strong className="break-all">{p.name}</strong>
        <Tag>{partnerStatus(p)}</Tag>
      </div>
      <p className="break-words text-sm">
        {names(brandIds(p))}
        {additions.length > 0 && (
          <span className="text-amber-700"> (+{names(additions)} 대기)</span>
        )}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>보유 키 {p.apiKeys.filter((k) => !k.revokedAt).length}개</span>
        <span>
          {p.webhook?.lastError
            ? '웹훅 최근 실패'
            : p.webhook?.lastSuccessAt
            ? '웹훅 정상'
            : p.webhook?.isEnabled
            ? '웹훅 사용 중'
            : '웹훅 꺼짐'}
        </span>
        {p.pendingChange && <span className="text-amber-700">변경 승인 대기</span>}
        <span>신청 {dateLabel(p.requestedAt ?? p.createdAt)}</span>
      </div>
      {p.rejectReason && (
        <p className="break-words text-sm text-red-600">{p.rejectReason}</p>
      )}
    </Link>
  );
};

export const AdminGiftIntegrationPage = () => {
  const { isAdmin, userProfile } = useAuth();
  const query = useAdminGiftPartners();
  const brandsQuery = useQuery(
    ['giftPartner', LowerRole.Admin, 'brands', userProfile?._id],
    () => listGiftItemBrand(LowerRole.Admin),
    { enabled: isAdmin() && !!query.data, retry: false },
  );
  if (!isAdmin())
    return <Alert type="warning" message="관리자만 API 연동을 관리할 수 있습니다." />;
  if (query.isLoading)
    return (
      <div className="p-4" role="status">
        연동 목록을 불러오는 중입니다.
      </div>
    );
  if (query.data === null) return <Navigate to="/gift-seller/brand" replace />;
  if (query.isError || brandsQuery.isError)
    return (
      <div className="p-4">
        <LoadError
          retry={() => {
            void query.refetch();
            void brandsQuery.refetch();
          }}
        />
      </div>
    );
  if (!query.data || !brandsQuery.data)
    return <div className="p-4">브랜드 목록을 불러오는 중입니다.</div>;
  const partners = query.data;
  const brands: OverviewBrand[] = brandsQuery.data.map((b) => ({
    _id: b._id,
    name: b.name,
    managedElsewhere: false,
    partner:
      partners.find((p) => p.status !== 'Rejected' && brandIds(p).includes(b._id))?._id ??
      null,
    pendingPartner:
      partners.find((p) => p.pendingChange?.giftItemBrands?.includes(b._id))?._id ?? null,
  }));
  return (
    <div className="mx-auto max-w-screen-xl p-4">
      <AdminIntegrationContent
        key={userProfile?._id}
        partners={partners}
        brands={brands}
      />
    </div>
  );
};

const AdminIntegrationContent = ({
  partners,
  brands,
}: {
  partners: GiftPartner[];
  brands: OverviewBrand[];
}) => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('requested');
  const [search, setSearch] = useState('');
  const [secret, setSecret] = useState<PartnerSecret & { id: string }>();
  const { run, busy, error } = useGiftPartnerAction();
  const selected = partners.find((p) => p._id === partnerId);
  if (selected)
    return (
      <GiftPartnerDetail
        key={selected._id}
        partner={selected}
        brands={brands}
        role={LowerRole.Admin}
        backPath={ADMIN_LIST_PATH}
      />
    );
  if (partnerId && partnerId !== 'new')
    return (
      <Alert
        type="warning"
        message="연동을 찾을 수 없습니다."
        description={<Link to={ADMIN_LIST_PATH}>목록으로 돌아가기</Link>}
      />
    );
  const create = (dto: GiftPartnerApplyDto & { memo?: string }) =>
    void run(async () => {
      const result = await createGiftPartner({
        name: dto.name,
        giftItemBrands: dto.giftItemBrands,
        allowedIps: dto.allowedIps,
        contact: dto.contact,
        memo: dto.memo,
      });
      setSecret({
        title: '연동키가 발급되었습니다',
        value: result.apiKey,
        partnerName: result.partner.name,
        id: result.partner._id,
      });
    });
  if (partnerId === 'new')
    return (
      <div className="space-y-4">
        <Link to={ADMIN_LIST_PATH} className="text-indigo-700">
          ← 연동 목록
        </Link>
        <PartnerSection title="운영자 직접 등록">
          <Alert
            type="warning"
            message="즉시 승인 상태로 등록하고 첫 연동키를 발급합니다. 콜백 URL은 등록 후 상세에서 설정하세요."
          />
          <ApplicationForm
            admin
            brands={brands}
            busy={busy || !!secret}
            error={error}
            onSubmit={create}
          />
        </PartnerSection>
        {secret && (
          <SecretModal
            secret={secret}
            onClose={() => {
              const id = secret.id;
              setSecret(undefined);
              navigate(`${ADMIN_PATH}/${id}`);
            }}
          />
        )}
      </div>
    );
  const requested = partners.filter((p) => p.status === 'Requested').length;
  const pending = partners.filter((p) => !!p.pendingChange).length;
  const filtered = partners.filter(
    (p) =>
      (tab === 'requested'
        ? p.status === 'Requested'
        : tab === 'pending'
        ? !!p.pendingChange
        : true) &&
      `${p.name} ${brandIds(p)
        .map((id) => {
          const included = p.giftItemBrands.find(
            (b) => typeof b !== 'string' && b._id === id,
          );
          return (
            brands.find((b) => b._id === id)?.name ??
            (typeof included === 'object' ? included.name : id)
          );
        })
        .join(' ')} ${p.contact?.name ?? ''}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold">공급사 API 연동 관리</h1>
        <Button onClick={() => navigate(`${ADMIN_PATH}/new`)}>운영자 직접 등록</Button>
      </div>
      <GiftPartnerGuideResources />
      <div className="flex flex-wrap gap-2">
        {[
          { value: 'requested', label: `승인 대기 ${requested}` },
          { value: 'pending', label: `변경 요청 ${pending}` },
          { value: 'all', label: `전체 ${partners.length}` },
        ].map((item) => (
          <Button
            key={item.value}
            type={tab === item.value ? 'primary' : 'default'}
            className={tab === item.value ? 'gift-partner-primary' : undefined}
            onClick={() => setTab(item.value)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      <Input
        aria-label="연동 검색"
        placeholder="연동 이름, 브랜드, 담당자 검색"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />
      {filtered.length === 0 && (
        <p className="rounded border bg-white p-6 text-center text-gray-500">
          표시할 연동이 없습니다.
        </p>
      )}
      <div className="space-y-3">
        {filtered.map((p) => (
          <div key={p._id} className="rounded-lg border border-gray-200 bg-white p-3">
            <PartnerListRow partner={p} brands={brands} basePath={ADMIN_PATH} />
            <dl className="mt-3 grid min-w-0 gap-2 px-1 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-gray-500">허용 IP</dt>
                <dd className="break-all">{p.allowedIps.join(', ') || '미등록'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">콜백 URL</dt>
                <dd className="break-all">{p.webhook?.url || '미등록'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">담당자</dt>
                <dd className="break-all">
                  {p.contact?.name || '미등록'} · {p.contact?.phoneNumber || '—'}
                  <br />
                  {p.contact?.email}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
};

const LoadError = ({ retry }: { retry: () => void }) => (
  <Alert
    type="error"
    message="연동 정보를 불러오지 못했습니다."
    description="네트워크와 접근 권한을 확인한 후 다시 시도해 주세요."
    action={<Button onClick={retry}>다시 시도</Button>}
  />
);
